import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";

/**
 * Per-tenant rename for a subscribed portal.
 * Submits PATCH /api/catalog/my-portals/{portal_id} with {display_name}.
 * Empty string resets to canonical name ($unset on backend).
 *
 * Props:
 *   portal       — the subscribed portal object (must have portal_id, display_name, canonical_name)
 *   open         — controlled open state
 *   onOpenChange — setter for open state
 *   onSuccess    — called after a successful save (triggers parent to re-fetch)
 */
export default function RenamePortalDialog({ portal, open, onOpenChange, onSuccess }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && portal) {
      // Pre-fill with the current display name (may equal canonical_name when not renamed)
      setValue(portal.display_name || portal.canonical_name || portal.portal_name || "");
    }
  }, [open, portal]);

  if (!portal) return null;

  const canonicalName = portal.canonical_name || portal.portal_name || "";
  const trimmed = value.trim();
  const saveDisabled = saving || trimmed.length === 0 || trimmed.length > 60;

  const handleSave = async () => {
    if (saveDisabled) return;
    setSaving(true);
    try {
      await api.patch(`/catalog/my-portals/${portal.portal_id}`, {
        display_name: trimmed,
      });
      toast.success("Renamed");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to rename portal"));
    } finally {
      setSaving(false);
    }
  };

  const handleUseDefault = async () => {
    setSaving(true);
    try {
      await api.patch(`/catalog/my-portals/${portal.portal_id}`, {
        display_name: "",
      });
      toast.success("Reset to default name");
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reset portal name"));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !saveDisabled) {
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename portal</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="rename-input">
              Your label for &ldquo;{canonicalName}&rdquo;
            </Label>
            <Input
              id="rename-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={60}
              autoComplete="off"
              autoFocus
            />
            <p className="text-xs text-gray-500">
              Only you see this name. It doesn&apos;t affect the underlying portal or your fund data.
            </p>
            {trimmed.length > 60 && (
              <p className="text-xs text-red-600">Name must be 60 characters or fewer.</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between gap-2">
          <Button
            variant="outline"
            onClick={handleUseDefault}
            disabled={saving}
            className="mr-auto"
          >
            Use default name
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="text-white"
              style={{ backgroundColor: BRAND.bg }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
